import { useNavigate } from '@solidjs/router'
import { Show, createSignal, onMount } from 'solid-js'
import { useAuth } from '../lib/auth'
import { demoAccountFor, isDemoHost, isDemoMode } from '../lib/demo'
import { isIOS, isStandalone, onInstallAvailability, promptInstall, readQueue } from '../lib/pwa'

function isVenueDomain(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.hostname === 'venue.zipcheck.my'
}

export default function Home() {
  const navigate = useNavigate()
  const auth = useAuth()

  const [installAvailable, setInstallAvailable] = createSignal(false)
  const [queued, setQueued] = createSignal(readQueue().length)
  const [demoError, setDemoError] = createSignal<string | null>(null)
  const [demoSigningIn, setDemoSigningIn] = createSignal(false)

  // Redirect to venue login if on venue domain
  onMount(() => {
    if (isVenueDomain()) {
      navigate('/venue-login', { replace: true })
    }
    onInstallAvailability(setInstallAvailable)
    window.addEventListener('online', () => setQueued(readQueue().length))

    // Demo host (demo.zipcheck.my, or any host with ?demo=1): sign the seeded
    // account in and go straight to the hub — the whole point is that a venue can
    // open the link and see the real product without an email round-trip.
    if (isDemoMode() && !auth.isAuthenticated()) {
      const { email } = demoAccountFor()
      setDemoSigningIn(true)
      void auth.signInDemo(email).then((ok) => {
        setDemoSigningIn(false)
        if (ok) navigate('/pass', { replace: true })
        else setDemoError(auth.error() ?? 'Demo sign-in failed')
      })
    }
  })

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      <header class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-md mx-auto flex items-center gap-2">
          <img src="/logo.svg" alt="ZipCheck" class="h-8" />
          <span class="text-lg font-semibold text-gray-900">ZipCheck</span>
          {isDemoHost() && <span class="ml-auto text-[10px] text-gray-400">demo</span>}
        </div>
        {/*
          Honest demo framing. A visitor sees a real-looking balance and real
          history; without this line they cannot tell the money is simulated.
          Deliberately visible rather than buried — the demo must not
          misrepresent itself as a live account.
        */}
        {isDemoHost() && (
          <div class="max-w-md mx-auto mt-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-1.5 text-[11px] text-amber-900">
            Demo — virtual money, no real charges. Data is reset periodically.
          </div>
        )}
      </header>

      <main class="flex-1 flex items-center justify-center p-4">
        <div class="w-full max-w-md text-center">
          <Show when={demoSigningIn()}>
            <p class="text-sm text-gray-500 py-8">Opening the ZipCheck demo…</p>
          </Show>
          <Show when={demoError()}>
            <div class="rounded-xl bg-red-50 border border-red-200 p-5 text-center">
              <p class="text-sm text-red-700">{demoError()}</p>
            </div>
          </Show>
          <Show when={auth.isAuthenticated()}>
            {/* Authenticated — redirect to pass */}
            {(() => {
              navigate('/pass', { replace: true })
              return null
            })()}
          </Show>

          <Show when={!auth.isAuthenticated() && !demoSigningIn() && !demoError()}>
            <div class="mb-8">
              <div class="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span class="text-4xl">🪪</span>
              </div>
              <h1 class="text-2xl font-bold text-gray-900 mb-2">Your digital wardrobe pass</h1>
              <p class="text-gray-600">Check in your items at any venue in seconds. No app download needed.</p>
            </div>

            {/* Installable: offer the app install in-app (Chrome/Edge/Android).
                iOS has no install event, so it gets a hint instead. */}
            <Show when={installAvailable()}>
              <button
                onClick={() => void promptInstall()}
                class="w-full mb-3 py-3 px-4 bg-white text-gray-900 font-semibold rounded-lg
                       border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Install ZipCheck on this device
              </button>
            </Show>
            <Show when={isIOS() && !isStandalone()}>
              <p class="text-[11px] text-gray-500 mb-3">
                On iPhone or iPad: tap <strong>Share</strong> → <strong>Add to Home Screen</strong>.
              </p>
            </Show>
            <Show when={queued() > 0}>
              <p class="text-[11px] text-amber-600 mb-3">
                {queued()} check-in{queued() > 1 ? 's' : ''} saved on this device, waiting to sync
              </p>
            </Show>

            <div class="space-y-3">
              <button
                onClick={() => navigate('/signup')}
                class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                       hover:bg-emerald-700 active:bg-emerald-800 transition-colors"
              >
                Get started
              </button>
              <button
                onClick={() => navigate('/venue-login')}
                class="w-full py-3 px-4 bg-white text-gray-700 font-medium rounded-lg
                       border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Venue login
              </button>
            </div>
          </Show>
        </div>
      </main>
    </div>
  )
}
